import type { ShapeType, WeaponType } from '../AppearanceDefinitions';
import { BODY_RIGS } from './HumanoidGeometry';
import type { Point } from './HumanoidGeometry';

export type HumanoidPose =
  | 'idle'
  | 'walkA'
  | 'walkB'
  | 'attack'
  | 'cast'
  | 'hurt'
  | 'jump'
  | 'fall'
  | 'death'
  | 'blink';

export interface AttachmentAnchor extends Point {
  angle: number;
}

export interface GhostPoseAnchors {
  backHand: AttachmentAnchor;
  frontHand: AttachmentAnchor;
  backFoot: AttachmentAnchor;
  frontFoot: AttachmentAnchor;
}

interface FootPoint extends Point {
  angle: number;
}

interface PoseGeometry {
  backHand: Point;
  frontHand: Point;
  backFoot: FootPoint;
  frontFoot: FootPoint;
}

const DEGREES_PER_RADIAN = 180 / Math.PI;
const DEFAULT_WEAPON: WeaponType = 'sword';

const IDLE_GEOMETRY: PoseGeometry = {
  backHand: { x: 35, y: 75 },
  frontHand: { x: 104, y: 70 },
  backFoot: { x: 47, y: 107, angle: -7 },
  frontFoot: { x: 86, y: 105, angle: 9 },
};

/** Pose coordinates are declarative so adding a pose is an exhaustive edit. */
const POSE_GEOMETRY: Record<Exclude<HumanoidPose, 'attack'>, PoseGeometry> = {
  idle: IDLE_GEOMETRY,
  blink: IDLE_GEOMETRY,
  walkA: {
    backHand: { x: 41, y: 67 },
    frontHand: { x: 110, y: 78 },
    backFoot: { x: 42, y: 104, angle: 10 },
    frontFoot: { x: 94, y: 110, angle: -5 },
  },
  walkB: {
    backHand: { x: 31, y: 81 },
    frontHand: { x: 103, y: 61 },
    backFoot: { x: 50, y: 111, angle: -10 },
    frontFoot: { x: 82, y: 103, angle: 7 },
  },
  cast: {
    backHand: { x: 32, y: 66 },
    frontHand: { x: 110, y: 50 },
    backFoot: { x: 43, y: 109, angle: -9 },
    frontFoot: { x: 96, y: 103, angle: 13 },
  },
  hurt: {
    backHand: { x: 25, y: 61 },
    frontHand: { x: 87, y: 81 },
    backFoot: { x: 39, y: 109, angle: -14 },
    frontFoot: { x: 81, y: 108, angle: -5 },
  },
  jump: {
    backHand: { x: 29, y: 62 },
    frontHand: { x: 104, y: 50 },
    backFoot: { x: 47, y: 102, angle: 20 },
    frontFoot: { x: 92, y: 98, angle: -17 },
  },
  fall: {
    backHand: { x: 25, y: 57 },
    frontHand: { x: 115, y: 68 },
    backFoot: { x: 44, y: 110, angle: -7 },
    frontFoot: { x: 93, y: 109, angle: 8 },
  },
  death: {
    backHand: { x: 31, y: 88 },
    frontHand: { x: 81, y: 91 },
    backFoot: { x: 47, y: 111, angle: -16 },
    frontFoot: { x: 83, y: 111, angle: 17 },
  },
};

const ATTACK_FRONT_HAND_X: Record<WeaponType, number> = {
  sword: 89,
  staff: 98,
  axe: 92,
  bow: 100,
  dagger: 100,
  mace: 96,
  wand: 98,
};

function attackGeometry(weaponType: WeaponType): PoseGeometry {
  return {
    backHand: weaponType === 'bow' ? { x: 81, y: 57 } : { x: 43, y: 82 },
    frontHand: { x: ATTACK_FRONT_HAND_X[weaponType], y: 57 },
    backFoot: { x: 45, y: 109, angle: -8 },
    frontFoot: { x: 99, y: 103, angle: 17 },
  };
}

function handAnchor(point: Point, shoulder: Point): AttachmentAnchor {
  return {
    ...point,
    angle: Math.round(
      Math.atan2(point.y - shoulder.y, point.x - shoulder.x) * DEGREES_PER_RADIAN,
    ),
  };
}

/**
 * Neutral silhouettes can use the full shape-specific hand landmark. Poses
 * that already extend a held item toward a viewBox edge use a bounded
 * envelope so wide bodies do not crop weapons or action silhouettes.
 */
function frontHandOffsetForPose(
  pose: HumanoidPose,
  authoredOffset: number,
): number {
  switch (pose) {
  case 'idle':
  case 'blink':
  case 'walkB':
  case 'hurt':
    return authoredOffset;
  case 'cast':
    return Math.min(authoredOffset, 4);
  case 'walkA':
  case 'attack':
  case 'jump':
  case 'fall':
  case 'death':
    return 0;
  }
}

/**
 * Canonical attachment points for the code-native humanoid skeleton.
 *
 * GearParts and WeaponParts consume the same immutable pose geometry, so held
 * equipment cannot drift from hands or accumulate transforms between frames.
 */
export function attachmentAnchorsForPose(
  pose: HumanoidPose,
  weaponType: WeaponType = DEFAULT_WEAPON,
  shape: ShapeType = 'classic',
): GhostPoseAnchors {
  const geometry = pose === 'attack' ? attackGeometry(weaponType) : POSE_GEOMETRY[pose];
  const rig = BODY_RIGS[shape];
  const { shoulders, handOffsetX, footOffsetX } = rig.attachments;
  const backHand = {
    x: geometry.backHand.x + handOffsetX.back,
    y: geometry.backHand.y,
  };
  const frontHand = {
    x: geometry.frontHand.x + frontHandOffsetForPose(pose, handOffsetX.front),
    y: geometry.frontHand.y,
  };

  return {
    backHand: handAnchor(backHand, shoulders.back),
    frontHand: handAnchor(frontHand, shoulders.front),
    backFoot: {
      ...geometry.backFoot,
      x: geometry.backFoot.x + footOffsetX.back,
    },
    frontFoot: {
      ...geometry.frontFoot,
      x: geometry.frontFoot.x + footOffsetX.front,
    },
  };
}
